import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaWhatsapp,
  FaEnvelope,
  FaMountain,
  FaUsers,
  FaMapMarkerAlt,
  FaTrophy,
  FaShieldAlt,
  FaAward,
  FaExternalLinkAlt,
} from 'react-icons/fa'
import aboutService from '../services/aboutService'
import SEO from '../utils/SEO'
import { generateFAQSchema } from '../utils/structuredData'
import Loader from '../components/Loader'

const ABOUT_FAQS = [
  {
    question: 'Who is Himachal Yatra?',
    answer:
      'Himachal Yatra is a premier travel booking platform specializing in Himachal Pradesh tours, adventure activities, hotel bookings, and customized travel services.',
  },
  {
    question: 'Is Himachal Yatra a registered travel company?',
    answer:
      'Yes, Himachal Yatra is a government-registered travel agency. We hold the HP Tourism Travel Agent Registration (No. 260226/71088, valid until 26 Apr 2029) issued by the Dept. of Tourism & Civil Aviation, Govt. of Himachal Pradesh, and the Udyam MSME Registration (UDYAM-HP-09-0036147) issued by the Ministry of MSME, Govt. of India. You can view our certificates on our Certifications page.',
  },
  {
    question: 'Can I customize my tour package?',
    answer:
      'Absolutely! We offer a custom trip planner where you can design your own itinerary. Our travel experts will help optimize your route, accommodation, and activities.',
  },
  {
    question: 'What safety measures does Himachal Yatra take?',
    answer:
      'We ensure all tours follow safety guidelines with experienced guides, insured vehicles, vetted accommodation partners, and 24/7 customer support during your trip.',
  },
]

const AboutUs = () => {
  const [about, setAbout] = useState(null)
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        const [aboutData, teamData] = await Promise.all([
          aboutService.getAboutContent(),
          aboutService.getTeamMembers(),
        ])
        if (aboutData.success) setAbout(aboutData.about)
        if (teamData.success) setTeam(teamData.team)
      } catch (error) {
        console.error('Error loading about us data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const socialLinks = about
    ? [
        {
          platform: 'facebook',
          url: about.social_facebook,
          icon: <FaFacebook size={22} />,
          label: 'Facebook',
        },
        {
          platform: 'twitter',
          url: about.social_twitter,
          icon: <FaTwitter size={22} />,
          label: 'Twitter',
        },
        {
          platform: 'instagram',
          url: about.social_instagram,
          icon: <FaInstagram size={22} />,
          label: 'Instagram',
        },
        {
          platform: 'linkedin',
          url: about.social_linkedin,
          icon: <FaLinkedin size={22} />,
          label: 'LinkedIn',
        },
        {
          platform: 'youtube',
          url: about.social_youtube,
          icon: <FaYoutube size={22} />,
          label: 'YouTube',
        },
        {
          platform: 'whatsapp',
          url: about.social_whatsapp
            ? `https://wa.me/${about.social_whatsapp.replace(/[^0-9]/g, '')}`
            : '',
          icon: <FaWhatsapp size={22} />,
          label: 'WhatsApp',
        },
      ].filter((l) => l.url && l.url.trim() !== '')
    : []

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size={80} />
      </div>
    )
  }

  const stats = about
    ? [
        {
          icon: <FaMountain className="text-blue-600 text-3xl" />,
          value: about.tours_count,
          label: 'Tours Offered',
        },
        {
          icon: <FaUsers className="text-green-600 text-3xl" />,
          value: about.customers_count,
          label: 'Happy Travellers',
        },
        {
          icon: <FaMapMarkerAlt className="text-red-600 text-3xl" />,
          value: about.destinations_count,
          label: 'Destinations',
        },
        {
          icon: <FaTrophy className="text-yellow-600 text-3xl" />,
          value: about.established,
          label: 'Est. Year',
        },
      ]
    : []

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="About Himachal Yatra - Your Trusted Travel Partner"
        description="Learn about Himachal Yatra, your trusted travel partner for Himachal Pradesh tours. Years of expertise in crafting unforgettable Himalayan experiences."
        canonical="/about"
        keywords="about Himachal Yatra, Himachal Pradesh travel company, trusted tour operator Himachal"
        structuredData={generateFAQSchema(ABOUT_FAQS)}
      />
      {/* Hero Section */}
      <div
        className="relative bg-gradient-to-r from-blue-900 to-blue-600 text-white py-24 px-4"
        style={{ backgroundImage: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)' }}
      >
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-4">{about?.company_name || 'Himachal Yatra'}</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">{about?.tagline}</p>

          {/* Social Media Links */}
          {socialLinks.length > 0 && (
            <div className="flex justify-center gap-4 mt-8">
              {socialLinks.map((link) => (
                <a
                  key={link.platform}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition"
                  aria-label={link.label}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stats Section */}
      {stats.length > 0 && (
        <div className="bg-white shadow-sm py-10">
          <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="flex flex-col items-center gap-2">
                {stat.icon}
                <span className="text-3xl font-bold text-gray-900">{stat.value}</span>
                <span className="text-gray-500 text-sm">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-16 space-y-16">
        {/* Our Story */}
        <section className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Story</h2>
            <p className="text-gray-600 leading-relaxed text-lg">{about?.story}</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img
              src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80"
              alt="Himalayan landscape"
              className="w-full h-72 object-cover"
            />
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="grid md:grid-cols-2 gap-8">
          <div className="bg-blue-50 rounded-2xl p-8 border border-blue-100">
            <h3 className="text-2xl font-bold text-blue-800 mb-3">Our Mission</h3>
            <p className="text-gray-700 leading-relaxed">{about?.mission}</p>
          </div>
          <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
            <h3 className="text-2xl font-bold text-green-800 mb-3">Our Vision</h3>
            <p className="text-gray-700 leading-relaxed">{about?.vision}</p>
          </div>
        </section>

        {/* Awards */}
        {about?.awards && (
          <section className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 text-center">
            <FaTrophy className="text-yellow-500 text-4xl mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Recognition & Awards</h3>
            <p className="text-gray-600">{about.awards}</p>
          </section>
        )}

        {/* Team Section */}
        {team.length > 0 && (
          <section>
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-2">Meet Our Team</h2>
            <p className="text-gray-500 text-center mb-10">
              The passionate people behind Himachal Yatra
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {team.map((member) => (
                <div key={member.id} className="card group">
                  <div className="relative overflow-hidden shine-on-hover">
                    {member.image_url ? (
                      <img
                        src={member.image_url}
                        alt={member.name}
                        className="w-full h-56 object-cover"
                      />
                    ) : (
                      <div className="w-full h-56 bg-gradient-to-br from-blue-400 to-blue-700 flex items-center justify-center">
                        <span className="text-white text-5xl font-bold">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-900">{member.name}</h4>
                    <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                    {member.bio && (
                      <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                    )}

                    <div className="flex gap-3 mt-4">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="text-gray-400 hover:text-blue-600 transition"
                          aria-label="Email"
                        >
                          <FaEnvelope size={18} />
                        </a>
                      )}
                      {member.linkedin_url && (
                        <a
                          href={member.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-700 transition"
                          aria-label="LinkedIn"
                        >
                          <FaLinkedin size={18} />
                        </a>
                      )}
                      {member.twitter_url && (
                        <a
                          href={member.twitter_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-sky-500 transition"
                          aria-label="Twitter"
                        >
                          <FaTwitter size={18} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* No team placeholder */}
        {team.length === 0 && (
          <section className="text-center py-12 text-gray-500">
            <FaUsers className="text-5xl mx-auto mb-4 text-gray-300" />
            <p>Team information will be available soon.</p>
          </section>
        )}

        {/* Certifications & Registrations */}
        <section>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Certifications &amp; Registrations
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Himachal Yatra is a government-registered travel agency. Our credentials are publicly
              verifiable.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {/* HP Tourism Certificate */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaShieldAlt className="text-blue-600" size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base leading-tight">
                    HP Tourism Travel Agent Registration
                  </h3>
                  <p className="text-gray-500 text-xs mt-1">
                    Govt. of Himachal Pradesh, Dept. of Tourism &amp; Civil Aviation
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500 block text-xs mb-0.5">Cert. No.</span>
                  <span className="font-semibold text-gray-800">260226/71088</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs mb-0.5">Valid Until</span>
                  <span className="font-semibold text-green-700">26 Apr 2029</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-auto">
                <Link
                  to="/certifications"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
                >
                  <FaShieldAlt size={13} />
                  View Certificate
                </Link>
                <a
                  href="https://eservices.himachaltourism.gov.in/certificate-validation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-blue-300 text-blue-700 hover:bg-blue-100 text-sm font-semibold px-4 py-2 rounded-lg transition"
                >
                  Verify <FaExternalLinkAlt size={11} />
                </a>
              </div>
            </div>

            {/* Udyam Certificate */}
            <div className="bg-green-50 border border-green-100 rounded-2xl p-6 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FaAward className="text-green-600" size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base leading-tight">
                    Udyam MSME Registration
                  </h3>
                  <p className="text-gray-500 text-xs mt-1">
                    Ministry of MSME, Govt. of India — Micro Enterprise
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2">
                  <span className="text-gray-500 block text-xs mb-0.5">Registration No.</span>
                  <span className="font-semibold text-gray-800">UDYAM-HP-09-0036147</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs mb-0.5">Date</span>
                  <span className="font-semibold text-gray-800">20 Feb 2026</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs mb-0.5">Category</span>
                  <span className="font-semibold text-gray-800">Travel Agency</span>
                </div>
              </div>

              <div className="mt-auto">
                <Link
                  to="/certifications"
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
                >
                  <FaAward size={13} />
                  View Certificate
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default AboutUs
